/**
 * Plain-language text about the site itself: the homepage's "About SuperFunUBG" section and the /about page.
 * scripts/prerender.mjs writes the same text into the page files, so it is readable before any script runs.
 */
import { CATALOG, CATEGORIES } from './catalog';

const count = CATALOG.length;
const solo = CATALOG.filter((g) => g.modes.includes('solo')).length;
const twoPlayer = CATALOG.filter((g) => g.modes.includes('keyboard') || g.modes.includes('touch')).length;
const byCategory = (id: string) => CATALOG.filter((g) => g.category === id).length;

/** Homepage section under the games. */
export const HOME_ABOUT: { heading: string; paragraphs: string[] } = {
  heading: 'About SuperFunUBG',
  paragraphs: [
    `SuperFunUBG is a free browser game site with ${count} original games that we design and build ourselves. Every game runs right in your web browser on a Chromebook, laptop, tablet or phone, with nothing to download, no account to make and nothing to buy.`,
    `You can play all ${solo} games on your own, and ${twoPlayer} of them also let two people play together on the same keyboard or by taking turns on one screen. Most two-player games come with a computer opponent that has easy, normal and hard levels, so you can practice before you challenge a friend.`,
    `The games cover ${CATEGORIES.length} kinds of play: ${CATEGORIES.map((c) => `${c.label.toLowerCase()} (${byCategory(c.id)})`).join(', ')}. Each game page explains the controls, gives a few tips and suggests similar games to try next. We add new games every week, and players can send us ideas with the Suggest a Game form.`,
  ],
};

/** The /about page. */
export const ABOUT_PAGE: { title: string; description: string; sections: Array<{ heading: string; paragraphs: string[] }> } = {
  title: 'About SuperFunUBG',
  description: `Who makes SuperFunUBG, how our ${count} original browser games are built, and what we do to keep the site fast, free and kid-friendly.`,
  sections: [
    {
      heading: 'What SuperFunUBG is',
      paragraphs: [
        `SuperFunUBG (playsuperfun.com) is a free collection of ${count} browser games made by Stuffly LLC, a small company in California. The name is short for "Super Fun Unblocked Games": games that open straight in a web browser, so they work on school Chromebooks and family computers without installing anything.`,
        'Our goal is simple: quick, fair, kid-friendly games that load in seconds, never ask for an account, and play well with a keyboard, a mouse or a touch screen.',
      ],
    },
    {
      heading: 'How we make the games',
      paragraphs: [
        'Every game on the site is original. We design the rules, characters and art ourselves and build each game from scratch for the web, using the browser’s own drawing tools. That is why the games load quickly and run smoothly even on older Chromebooks.',
        'Many games are our own take on popular kinds of play, such as racing, sports, clicker, puzzle and party games, with new names, new characters and our own twists. Each game is tested to make sure it can be finished, that the computer opponents are fair on every difficulty level, and that it works on small screens.',
      ],
    },
    {
      heading: 'Playing alone or with a friend',
      paragraphs: [
        `All ${count} games can be played solo. ${twoPlayer} of them can also be played by two people on one device: one keyboard split in half (Player 1 uses W A S D and Space, Player 2 uses the arrow keys and Enter), or by passing the device back and forth in turn-based games like chess and checkers.`,
        'There is no online chat and no playing with strangers. Opponents are always computer players or a friend sitting next to you.',
      ],
    },
    {
      heading: 'Kid-friendly by design',
      paragraphs: [
        'Action in our games is cartoon-style: foam blasters, bonks and splats, never blood or gore. There are no accounts, no chat and no purchases. Game progress, favorites and settings are saved only in your own browser.',
        'Parents and teachers can read more on our For Parents and Teachers page, and the full details of what we do and do not collect are in our Privacy Policy.',
      ],
    },
    {
      heading: 'New games every week',
      paragraphs: [
        'We add new games regularly and keep improving the ones already on the site. New games get a NEW badge on the homepage for their first two weeks. If you have an idea for a game, send it with the Suggest a Game form at the bottom of the homepage.',
      ],
    },
    {
      heading: 'Contact',
      paragraphs: ['Questions, ideas or a problem with a game? Email us at support@stuffly.co and we will get back to you.'],
    },
  ],
};
